import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import {
  ChevronLeft, ChevronRight, Loader2, Clock, CheckCircle2, AlertTriangle,
  Calendar as CalendarIcon, Trophy,
} from "lucide-react";

/**
 * V2 / Mobile-native My Matches.
 * Lists every match the viewer is in, grouped by date, with status pills.
 * Reuses GET /matches/my/all (unchanged).
 */
export default function MyMatchesV2() {
  const { user } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [matches, setMatches] = useState(null);
  const [filter, setFilter] = useState("upcoming"); // upcoming | recent | all

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/matches/my/all");
        if (!cancelled) setMatches(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setMatches([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const todayStr = new Date().toISOString().slice(0, 10);

  const filteredAndGrouped = useMemo(() => {
    if (!matches) return [];
    let arr = matches;
    if (filter === "upcoming") {
      arr = matches.filter((m) => m.status !== "confirmed" || (m.scheduled_at && m.scheduled_at >= todayStr));
    } else if (filter === "recent") {
      arr = matches.filter((m) => m.status === "confirmed" || m.status === "disputed");
    }
    // Group by date
    const out = new Map();
    for (const m of arr) {
      const k = m.scheduled_at || "TBD";
      if (!out.has(k)) out.set(k, []);
      out.get(k).push(m);
    }
    return Array.from(out.entries()).sort((a, b) => {
      if (a[0] === "TBD") return 1;
      if (b[0] === "TBD") return -1;
      // upcoming → ascending, recent → descending so most recent first
      return filter === "recent" ? b[0].localeCompare(a[0]) : a[0].localeCompare(b[0]);
    });
  }, [matches, filter, todayStr]);

  return (
    <div className="px-5 pt-5 pb-6 space-y-4" data-testid="v2-my-matches">
      <button
        type="button"
        onClick={() => nav(-1)}
        className="inline-flex items-center gap-1.5 text-[#A1A1AA] hover:text-white text-xs font-mono uppercase tracking-[0.25em] transition-colors"
        data-testid="v2-mm-back"
      >
        <ChevronLeft className="w-4 h-4" /> {t("v2.match.back")}
      </button>

      <header>
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#A5B4FC]">{t("v2.mm.kicker")}</div>
        <h1 className="text-2xl font-bold text-white mt-1">{t("v2.mm.title")}</h1>
      </header>

      {/* Filter tabs */}
      <div className="flex gap-1 p-1 bg-[#0F0F18] rounded-2xl border border-[#1F1F2A]" data-testid="v2-mm-filters">
        {["upcoming", "recent", "all"].map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            data-testid={`v2-mm-filter-${k}`}
            className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${
              filter === k
                ? "bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white shadow-lg shadow-[#7C3AED]/20"
                : "text-[#71717A] hover:text-white"
            }`}
          >
            {t(`v2.mm.filter.${k}`)}
          </button>
        ))}
      </div>

      {/* List */}
      {matches === null ? (
        <div className="flex items-center justify-center py-10 text-[#52525B]">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : filteredAndGrouped.length === 0 ? (
        <div className="rounded-2xl bg-[#12121C] border border-dashed border-[#1F1F2A] p-8 text-center" data-testid="v2-mm-empty">
          <CalendarIcon className="w-7 h-7 text-[#52525B] mx-auto mb-2" />
          <div className="text-sm text-[#71717A]">{t(`v2.mm.empty.${filter}`)}</div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="v2-mm-list">
          {filteredAndGrouped.map(([day, list]) => (
            <DayBlock key={day} day={day} matches={list} viewerId={user?.id} todayStr={todayStr} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function DayBlock({ day, matches, viewerId, todayStr, t }) {
  const isTBD = day === "TBD";
  const isToday = !isTBD && day === todayStr;
  const isPast = !isTBD && day < todayStr;

  let weekday = "", monthDay = "", relative = "";
  if (!isTBD) {
    const d = new Date(`${day}T00:00:00`);
    if (!isNaN(d.getTime())) {
      weekday = d.toLocaleDateString(undefined, { weekday: "short" });
      monthDay = d.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
      const diffDays = Math.round((d.getTime() - new Date(todayStr + "T00:00:00").getTime()) / 86400000);
      if (diffDays === 0) relative = t("v2.cal.today");
      else if (diffDays === 1) relative = t("v2.cal.tomorrow");
      else if (diffDays === -1) relative = t("v2.cal.yesterday");
      else if (diffDays > 1 && diffDays <= 7) relative = t("v2.cal.inDays", { n: diffDays });
      else if (diffDays < -1 && diffDays >= -7) relative = t("v2.cal.daysAgo", { n: Math.abs(diffDays) });
    }
  }

  const tileBg = isToday ? "bg-[#7C3AED]" : isPast ? "bg-[#1F1F2A]" : "bg-[#12121C] border border-[#27272F]";

  return (
    <section data-testid={`v2-mm-day-${day}`}>
      <header className="flex items-end gap-3 mb-2 px-1">
        <div className={`rounded-2xl px-3 py-2 min-w-[60px] text-center shadow-md text-white ${tileBg}`}>
          {isTBD ? (
            <>
              <div className="text-[9px] font-bold uppercase tracking-[0.25em] opacity-70">{t("v2.cal.tbd.line1")}</div>
              <div className="text-base font-bold leading-tight mt-0.5">{t("v2.cal.tbd.line2")}</div>
            </>
          ) : (
            <>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] opacity-80">{weekday}</div>
              <div className="text-lg font-bold leading-tight mt-0.5">{monthDay}</div>
            </>
          )}
        </div>
        <div className="flex-1 pb-1">
          {!isTBD && relative && (
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#A5B4FC]">{relative}</div>
          )}
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#52525B]">
            {t("v2.cal.matchCount", { n: matches.length })}
          </div>
        </div>
      </header>
      <div className="space-y-1.5 ml-[68px]">
        {matches.map((m) => <Row key={m.id} m={m} viewerId={viewerId} t={t} />)}
      </div>
    </section>
  );
}

function Row({ m, viewerId, t }) {
  const isHome = m.home_id === viewerId;
  const opp = isHome ? m.away : m.home;
  const oppName = opp?.username || "?";
  const myScore = isHome ? m.confirmed_score_home : m.confirmed_score_away;
  const oppScore = isHome ? m.confirmed_score_away : m.confirmed_score_home;
  const isConfirmed = m.status === "confirmed";

  let StatusIcon = Clock;
  let statusColor = "#A1A1AA";
  let statusLabel = t("v2.match.status.scheduled");
  if (isConfirmed) {
    StatusIcon = CheckCircle2;
    if (myScore > oppScore) { statusColor = "#10B981"; statusLabel = t("v2.mm.won"); }
    else if (myScore < oppScore) { statusColor = "#EF4444"; statusLabel = t("v2.mm.lost"); }
    else { statusColor = "#F59E0B"; statusLabel = t("v2.mm.drawn"); }
  } else if (m.status === "disputed") {
    StatusIcon = AlertTriangle; statusColor = "#E11D48"; statusLabel = t("v2.match.status.disputed");
  } else if (m.status === "pending") {
    statusColor = "#F59E0B"; statusLabel = t("v2.match.status.pending");
  }

  return (
    <Link
      to={`/v2/matches/${m.id}`}
      data-testid={`v2-mm-row-${m.id}`}
      className="flex items-center justify-between gap-2 rounded-xl bg-[#12121C] border border-[#1F1F2A] hover:bg-[#161620] hover:border-[#7C3AED]/40 px-3 py-2.5 transition-colors"
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div className="flex flex-col">
          <span
            className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-widest"
            style={{ color: statusColor }}
          >
            <StatusIcon className="w-2.5 h-2.5" /> {statusLabel}
          </span>
        </div>
        <div className="min-w-0">
          <div className="text-sm text-white truncate">
            <span className="text-[#52525B] text-[10px] mr-1">{isHome ? "H" : "A"} ·</span>
            <span className="font-semibold">{oppName}</span>
          </div>
          <div className="text-[10px] text-[#71717A] truncate">
            {m.league?.name || "—"}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {isConfirmed ? (
          <span className="text-lg font-bold tabular-nums" style={{ color: statusColor }}>
            {myScore}–{oppScore}
          </span>
        ) : (
          <Trophy className="w-4 h-4 text-[#52525B]" />
        )}
        <ChevronRight className="w-4 h-4 text-[#52525B]" />
      </div>
    </Link>
  );
}
